// @mui
import { useSettingsContext } from 'src/components/settings';
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState } from 'react';
// components
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import useHeroSection from 'src/hooks/use-homepage-data';
import { useSnackbar } from 'notistack';
import { postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';

// ----------------------------------------------------------------------

export default function ContactUsSection() {
  const settings = useSettingsContext();
  const homePageData = useSelector((state) => state.website.homePage);
  const { fetchOnMount } = useHeroSection();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    // Section Headers
    mainTitle: "",
    mainTitleColor: "#333333",
    subtitle: "",
    subtitleColor: "#666666",
    backgroundColor: "#ffffff",

    // Contact Information
    contactInfo: {
      phone: "",
      phoneLabel: "",
      phoneColor: "#333333",
      phoneHref: "",
      liveChatLabel: "",
      liveChatColor: "#333333",
      email: "",
      emailLabel: "",
      emailColor: "#333333",
      emailHref: "",
    },

    // Social Media Links
    socialMedia: {
      title: "Follow Us",
      titleColor: "#333333",
      links: [
        { name: "", url: "", icon: "", color: "#8BC34A" },
        { name: "", url: "", icon: "", color: "#8BC34A" },
        { name: "", url: "", icon: "", color: "#8BC34A" },
        { name: "", url: "", icon: "", color: "#8BC34A" },
        { name: "", url: "", icon: "", color: "#8BC34A" },
        { name: "", url: "", icon: "", color: "#8BC34A" }
      ]
    },

    // Download Links
    downloadLinks: [
      {
        name: "",
        url: "",
        label: "",
        backgroundColor: "#8BC34A",
        textColor: "#ffffff"
      },
      {
        name: "",
        url: "",
        label: "",
        backgroundColor: "#8BC34A",
        textColor: "#ffffff"
      },
      {
        name: "",
        url: "",
        label: "",
        backgroundColor: "#8BC34A",
        textColor: "#ffffff"
      }
    ],

    // Contact Icons Colors
    contactIconColor: "#8BC34A",
    contactIconBackgroundColor: "#f0f8e8"
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const updateSocialLink = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        links: prev.socialMedia.links.map((link, i) =>
          i === index ? { ...link, [field]: value } : link
        )
      }
    }));
  };

  const updateDownloadLink = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      downloadLinks: prev.downloadLinks.map((link, i) =>
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  const addSocialLink = () => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        links: [...prev.socialMedia.links, {
          name: "New Platform",
          url: "https://example.com",
          icon: "link",
          color: "#8BC34A"
        }]
      }
    }));
  };

  const removeSocialLink = (index) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        links: prev.socialMedia.links.filter((_, i) => i !== index)
      }
    }));
  };

  const addDownloadLink = () => {
    setFormData(prev => ({
      ...prev,
      downloadLinks: [...prev.downloadLinks, {
        name: "New Platform",
        url: "https://example.com",
        label: "Download Now",
        backgroundColor: "#8BC34A",
        textColor: "#ffffff"
      }]
    }));
  };

  const removeDownloadLink = (index) => {
    setFormData(prev => ({
      ...prev,
      downloadLinks: prev.downloadLinks.filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    fetchOnMount();
  }, [fetchOnMount]);

  useEffect(() => {
    if (homePageData?.contact_us_section) {
      const ContactUsSection = JSON.parse(JSON.stringify(homePageData.contact_us_section));
      setFormData(prev => ({
        ...prev,
        ...ContactUsSection
      }));
    }
  }, [homePageData]);

  const handleSave = async () => {
    setLoading(true);
    // const updatedData = {
    //   ...homePageData
    // }
    // delete updatedData.contact_us_section;
    // console.log("🚀 ~ handleSave ~ updatedData:", updatedData);
    try {
      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.home_page.hero_section,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(homePageData)),
            contact_us_section: JSON.parse(JSON.stringify(formData))
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  const ColorPreview = ({ color, size = 24 }) => (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '4px',
        backgroundColor: color,
        border: '1px solid #ddd',
        display: 'inline-block',
        mr: 1
      }}
    />
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Contact Us Section
        </Typography>

        {/* Section Headers */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Section Headers</Typography>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Main Title</Typography>
            <TextField
              fullWidth
              value={formData.mainTitle}
              onChange={(e) => updateFormData('mainTitle', e.target.value)}
              placeholder="Enter main title"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Title Color:</Typography>
              <ColorPreview color={formData.mainTitleColor} />
              <TextField
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Subtitle Description</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={formData.subtitle}
              onChange={(e) => updateFormData('subtitle', e.target.value)}
              placeholder="Enter subtitle description"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Subtitle Color:</Typography>
              <ColorPreview color={formData.subtitleColor} />
              <TextField
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Background Color</Typography>
            <Stack direction="row" alignItems="center" spacing={2}>
              <ColorPreview color={formData.backgroundColor} />
              <TextField
                type="color"
                value={formData.backgroundColor}
                onChange={(e) => updateFormData('backgroundColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Contact Information</Typography>
          </Stack>

          <Grid container spacing={3}>
            {/* Phone Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Phone Details</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.contactInfo.phone}
                    onChange={(e) => updateFormData('contactInfo.phone', e.target.value)}
                    placeholder="Enter phone number"
                  />
                  <TextField
                    fullWidth
                    label="Phone Label"
                    value={formData.contactInfo.phoneLabel}
                    onChange={(e) => updateFormData('contactInfo.phoneLabel', e.target.value)}
                    placeholder="Enter phone label"
                  />
                  <TextField
                    fullWidth
                    label="Phone Href Link"
                    value={formData.contactInfo.phoneHref}
                    onChange={(e) => updateFormData('contactInfo.phoneHref', e.target.value)}
                    placeholder="Enter phone href (e.g., tel:+15162591939)"
                    helperText="Use tel: prefix for click-to-call functionality"
                  />
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2">Phone Color:</Typography>
                    <ColorPreview color={formData.contactInfo.phoneColor} />
                    <TextField
                      type="color"
                      value={formData.contactInfo.phoneColor}
                      onChange={(e) => updateFormData('contactInfo.phoneColor', e.target.value)}
                      sx={{ width: 100 }}
                    />
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Email Information */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Email Details</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={formData.contactInfo.email}
                    onChange={(e) => updateFormData('contactInfo.email', e.target.value)}
                    placeholder="Enter email address"
                  />
                  <TextField
                    fullWidth
                    label="Email Label"
                    value={formData.contactInfo.emailLabel}
                    onChange={(e) => updateFormData('contactInfo.emailLabel', e.target.value)}
                    placeholder="Enter email label"
                  />
                  <TextField
                    fullWidth
                    label="Email Href Link"
                    value={formData.contactInfo.emailHref}
                    onChange={(e) => updateFormData('contactInfo.emailHref', e.target.value)}
                    placeholder="Enter email href (e.g., mailto:support@ezelogs.com)"
                    helperText="Use mailto: prefix for click-to-email functionality"
                  />
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2">Email Color:</Typography>
                    <ColorPreview color={formData.contactInfo.emailColor} />
                    <TextField
                      type="color"
                      value={formData.contactInfo.emailColor}
                      onChange={(e) => updateFormData('contactInfo.emailColor', e.target.value)}
                      sx={{ width: 100 }}
                    />
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Live Chat Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Live Chat Details</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Live Chat Label"
                    value={formData.contactInfo.liveChatLabel}
                    onChange={(e) => updateFormData('contactInfo.liveChatLabel', e.target.value)}
                    placeholder="Enter live chat label"
                  />
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2">Live Chat Color:</Typography>
                    <ColorPreview color={formData.contactInfo.liveChatColor} />
                    <TextField
                      type="color"
                      value={formData.contactInfo.liveChatColor}
                      onChange={(e) => updateFormData('contactInfo.liveChatColor', e.target.value)}
                      sx={{ width: 100 }}
                    />
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Contact Icons Styling</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>Icon Color</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ColorPreview color={formData.contactIconColor} />
                  <TextField
                    type="color"
                    value={formData.contactIconColor}
                    onChange={(e) => updateFormData('contactIconColor', e.target.value)}
                    sx={{ width: 100 }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>Icon Background Color</Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <ColorPreview color={formData.contactIconBackgroundColor} />
                  <TextField
                    type="color"
                    value={formData.contactIconBackgroundColor}
                    onChange={(e) => updateFormData('contactIconBackgroundColor', e.target.value)}
                    sx={{ width: 100 }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Social Media Links */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Social Media Links</Typography>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Social Media Title</Typography>
            <TextField
              fullWidth
              value={formData.socialMedia.title}
              onChange={(e) => updateFormData('socialMedia.title', e.target.value)}
              placeholder="Enter social media section title"
              sx={{ mb: 2 }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">Title Color:</Typography>
              <ColorPreview color={formData.socialMedia.titleColor} />
              <TextField
                type="color"
                value={formData.socialMedia.titleColor}
                onChange={(e) => updateFormData('socialMedia.titleColor', e.target.value)}
                sx={{ width: 100 }}
              />
            </Stack>
          </Box>

          <Grid container spacing={2}>
            {formData.socialMedia.links.map((link, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.neutral' }}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">{link.name}</Typography>
                      <Button
                        color="error"
                        size="small"
                        startIcon={<Iconify icon="mingcute:close-line" />}
                        onClick={() => removeSocialLink(index)}
                      >
                        Remove
                      </Button>
                    </Stack>

                    <TextField
                      fullWidth
                      label="Platform Name"
                      value={link.name}
                      onChange={(e) => updateSocialLink(index, 'name', e.target.value)}
                      size="small"
                    />

                    <TextField
                      fullWidth
                      label="URL"
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                      size="small"
                    />

                    <TextField
                      fullWidth
                      label="Icon Name"
                      value={link.icon}
                      onChange={(e) => updateSocialLink(index, 'icon', e.target.value)}
                      size="small"
                      helperText="Icon identifier (e.g., facebook, twitter, linkedin)"
                    />

                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body2">Color:</Typography>
                      <ColorPreview color={link.color} size={20} />
                      <TextField
                        type="color"
                        value={link.color}
                        onChange={(e) => updateSocialLink(index, 'color', e.target.value)}
                        sx={{ width: 70 }}
                      />
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Button
            color="success"
            variant="outlined"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={addSocialLink}
            sx={{ mt: 2 }}
          >
            Add Social Media Link
          </Button>
        </Box>

        {/* Download Links */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Download Links</Typography>
          </Stack>

          <Grid container spacing={2}>
            {formData.downloadLinks.map((link, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.neutral' }}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">{link.name}</Typography>
                      <Button
                        color="error"
                        size="small"
                        startIcon={<Iconify icon="mingcute:close-line" />}
                        onClick={() => removeDownloadLink(index)}
                      >
                        Remove
                      </Button>
                    </Stack>

                    <TextField
                      fullWidth
                      label="Platform Name"
                      value={link.name}
                      onChange={(e) => updateDownloadLink(index, 'name', e.target.value)}
                      size="small"
                    />

                    <TextField
                      fullWidth
                      label="URL"
                      value={link.url}
                      onChange={(e) => updateDownloadLink(index, 'url', e.target.value)}
                      size="small"
                    />

                    <TextField
                      fullWidth
                      label="Button Label"
                      value={link.label}
                      onChange={(e) => updateDownloadLink(index, 'label', e.target.value)}
                      size="small"
                    />

                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Background</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={link.backgroundColor} size={20} />
                          <TextField
                            type="color"
                            value={link.backgroundColor}
                            onChange={(e) => updateDownloadLink(index, 'backgroundColor', e.target.value)}
                            sx={{ width: 60 }}
                          />
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ mb: 1 }}>Text Color</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ColorPreview color={link.textColor} size={20} />
                          <TextField
                            type="color"
                            value={link.textColor}
                            onChange={(e) => updateDownloadLink(index, 'textColor', e.target.value)}
                            sx={{ width: 60 }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* <Button
            color="success"
            variant="outlined"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={addDownloadLink}
            sx={{ mt: 2 }}
          >
            Add Download Link
          </Button> */}
        </Box>

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="eva:save-fill" />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
